import { useState, useEffect } from 'react';
import { useContributionCalculator } from '@hooks/useContributionCalculator';
import { ContributionPreview } from './ContributionPreview';
import { FormatTypeSelector } from './FormatTypeSelector';
import type {
  NIBFormatType,
  NIBPayIndicator,
  NIBContributionCalculatorInput,
} from '@/types';
import {
  NIBCategory,
  NIB_PAY_INDICATOR_LABELS,
  NIB_CATEGORY_INFO,
} from '@/types';
import {
  CalculatorIcon,
  ArrowPathIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

interface ContributionCalculatorProps {
  defaultFormatType?: NIBFormatType;
  defaultPayIndicator?: NIBPayIndicator;
  defaultCategory?: NIBCategory;
  className?: string;
}

export const ContributionCalculator = ({
  defaultFormatType = 'NON_HOSPITALITY',
  defaultPayIndicator = 'M',
  defaultCategory = NIBCategory.REGULAR,
  className = '',
}: ContributionCalculatorProps) => {
  const { preview, wagePreview, isCalculating, error, calculate, reset } =
    useContributionCalculator();

  // Form state
  const [grossWages, setGrossWages] = useState<string>('');
  const [weeks, setWeeks] = useState<number>(4);
  const [payIndicator, setPayIndicator] =
    useState<NIBPayIndicator>(defaultPayIndicator);
  const [category, setCategory] = useState<NIBCategory>(defaultCategory);
  const [formatType, setFormatType] =
    useState<NIBFormatType>(defaultFormatType);
  const [gratuityWages, setGratuityWages] = useState<string>('');

  // Auto-calculate when inputs change
  useEffect(() => {
    const wages = parseFloat(grossWages) || 0;
    const tips = parseFloat(gratuityWages) || 0;

    if (wages > 0) {
      const input: NIBContributionCalculatorInput = {
        original_wage: wages,
        weeks: weeks,
        pay_indicator: payIndicator,
        category: category,
        gratuity_wages: formatType === 'HOSPITALITY' ? tips : undefined,
      };
      calculate(input);
    } else {
      reset();
    }
  }, [
    grossWages,
    weeks,
    payIndicator,
    category,
    formatType,
    gratuityWages,
    calculate,
    reset,
  ]);

  const handleReset = () => {
    setGrossWages('');
    setWeeks(4);
    setPayIndicator(defaultPayIndicator);
    setCategory(defaultCategory);
    setFormatType(defaultFormatType);
    setGratuityWages('');
    reset();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-BS', {
      style: 'currency',
      currency: 'BSD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Calculator Form */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-5 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CalculatorIcon className="h-6 w-6 text-primary-600" />
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Contribution Calculator
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Calculate NIB contributions for any wage amount
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleReset}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <ArrowPathIcon className="h-4 w-4" />
              Reset
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            {/* Gross Wages */}
            <div>
              <label
                htmlFor="gross-wages"
                className="block text-sm font-medium text-gray-700"
              >
                Gross Wages
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">$</span>
                </div>
                <input
                  type="number"
                  id="gross-wages"
                  value={grossWages}
                  onChange={e => setGrossWages(e.target.value)}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  className="focus:ring-primary-500 focus:border-primary-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Enter the total gross wages for the period
              </p>
            </div>

            {/* Pay Indicator */}
            <div>
              <label
                htmlFor="pay-indicator"
                className="block text-sm font-medium text-gray-700"
              >
                Pay Frequency
              </label>
              <select
                id="pay-indicator"
                value={payIndicator}
                onChange={e =>
                  setPayIndicator(e.target.value as NIBPayIndicator)
                }
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
              >
                {Object.entries(NIB_PAY_INDICATOR_LABELS).map(
                  ([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  )
                )}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                How often employees are paid
              </p>
            </div>

            {/* Weeks */}
            <div>
              <label
                htmlFor="weeks"
                className="block text-sm font-medium text-gray-700"
              >
                Weeks in Period
              </label>
              <select
                id="weeks"
                value={weeks}
                onChange={e => setWeeks(parseInt(e.target.value))}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
              >
                {[1, 2, 3, 4, 5].map(w => (
                  <option key={w} value={w}>
                    {w} {w === 1 ? 'week' : 'weeks'}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Number of weeks in the pay period
              </p>
            </div>

            {/* Employee Category */}
            <div>
              <label
                htmlFor="employee-category"
                className="block text-sm font-medium text-gray-700"
              >
                Employee Type
              </label>
              <select
                id="employee-category"
                value={category}
                onChange={e =>
                  setCategory(parseInt(e.target.value) as NIBCategory)
                }
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
              >
                {Object.entries(NIB_CATEGORY_INFO).map(([catNum, info]) => (
                  <option key={catNum} value={catNum}>
                    {info.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Standard {NIB_CATEGORY_INFO[category].employeeRate * 100}% +{' '}
                {NIB_CATEGORY_INFO[category].employerRate * 100}% rates (July
                2024)
              </p>
            </div>

            {/* Gratuity Wages (only for HOSPITALITY) */}
            {formatType === 'HOSPITALITY' && (
              <div>
                <label
                  htmlFor="gratuity-wages"
                  className="block text-sm font-medium text-gray-700"
                >
                  Gratuity/Tips (Optional)
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">$</span>
                  </div>
                  <input
                    type="number"
                    id="gratuity-wages"
                    value={gratuityWages}
                    onChange={e => setGratuityWages(e.target.value)}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    className="focus:ring-primary-500 focus:border-primary-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Tips/gratuity taxed at 9.8%
                </p>
              </div>
            )}
          </div>

          {/* Format Type */}
          <div className="mt-6">
            <FormatTypeSelector
              value={formatType}
              onChange={setFormatType}
              disabled={false}
            />
          </div>

          {/* Error Display */}
          {error && (
            <div className="mt-4 rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    Calculation Error
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>{error.message}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Wage Processing Info */}
      {wagePreview && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <InformationCircleIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-blue-900">
                Wage Processing Breakdown
              </h4>
              <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <div>
                  <dt className="text-blue-700">Original Wages:</dt>
                  <dd className="font-medium text-blue-900">
                    {formatCurrency(parseFloat(wagePreview.original_wage))}
                  </dd>
                </div>
                {(() => {
                  const centsRemoved =
                    parseFloat(wagePreview.original_wage) -
                    parseFloat(wagePreview.truncated_wage);
                  return (
                    centsRemoved > 0 && (
                      <div>
                        <dt className="text-blue-700">Cents Removed:</dt>
                        <dd className="font-medium text-blue-900">
                          ${centsRemoved.toFixed(2)}
                        </dd>
                      </div>
                    )
                  );
                })()}
                <div>
                  <dt className="text-blue-700">Truncated Wages:</dt>
                  <dd className="font-medium text-blue-900">
                    {formatCurrency(parseFloat(wagePreview.truncated_wage))}
                  </dd>
                </div>
                {wagePreview.was_capped && (
                  <div>
                    <dt className="text-blue-700">Weekly Cap Applied:</dt>
                    <dd className="font-medium text-blue-900">
                      {formatCurrency(
                        parseFloat(wagePreview.truncated_wage) -
                          parseFloat(wagePreview.capped_wage)
                      )}
                    </dd>
                  </div>
                )}
                <div className="col-span-2 pt-2 border-t border-blue-300">
                  <dt className="text-blue-700">Final Insurable Wages:</dt>
                  <dd className="font-semibold text-blue-900 text-base">
                    {formatCurrency(parseFloat(wagePreview.capped_wage))}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      )}

      {/* Contribution Preview */}
      <ContributionPreview
        preview={preview}
        category={category}
        grossWages={parseFloat(grossWages) || undefined}
        isLoading={isCalculating}
        showDetails={true}
      />
    </div>
  );
};
